import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { batchesAPI, productsAPI } from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import { Factory, Calendar, Package, AlertTriangle, Search, Edit, Trash2, Plus, Eye } from 'lucide-react';
import { BatchFormModal } from '../../components/batches/BatchFormModal';
import { BatchViewModal } from '../../components/batches/BatchViewModal';
import { BatchDeleteModal } from '../../components/batches/BatchDeleteModal';

interface Product {
  product_id: number;
  name: string;
  description?: string;
  price: string | number;
  status: 'Active' | 'Inactive' | 'Discontinued';
  created_at: string;
  updated_at: string;
}

interface Batch {
  batch_id: number;
  product_id: number;
  production_date: string;
  created_at: string;
  batch_number?: string;
  product?: Product;
  _count?: {
    complaints: number;
  };
}

interface BatchFilters {
  product_id?: number;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const BatchesPage: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [filters, setFilters] = useState<BatchFilters>({
    page: 1,
    limit: 10,
    sortBy: 'production_date',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [batchStats, setBatchStats] = useState({
    total: 0,
    thisMonth: 0,
    withComplaints: 0,
    recentBatches: 0,
  });
  const { addToast } = useToast();

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await batchesAPI.getAll(filters);
      setBatches(response.data.batches);
      setPagination(response.data.pagination);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', message: error.message || 'Failed to fetch batches' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ status: 'Active' });
      setProducts(response.data.products);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', message: error.message || 'Failed to fetch products' });
    }
  };

  const fetchBatchStats = async () => {
    try {
      const statsParams = { page: 1, limit: 1000 };
      const response = await batchesAPI.getAll(statsParams);
      
      if (response.success) {
        const allBatches = response.data.batches || [];
        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const stats = {
          total: response.data.pagination.total,
          thisMonth: allBatches.filter((b: Batch) => new Date(b.production_date) >= thisMonth).length,
          withComplaints: allBatches.filter((b: Batch) => (b._count?.complaints || 0) > 0).length,
          recentBatches: allBatches.filter((b: Batch) => new Date(b.created_at) >= lastWeek).length,
        };
        setBatchStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch batch stats:', error);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [filters]);

  useEffect(() => {
    fetchProducts();
    fetchBatchStats();
  }, []);

  const handleCreate = () => {
    setSelectedBatch(null);
    setFormModalOpen(true);
  };

  const handleEdit = (batch: Batch) => {
    setSelectedBatch(batch);
    setFormModalOpen(true);
  };

  const handleView = (batch: Batch) => {
    setSelectedBatch(batch);
    setViewModalOpen(true);
  };

  const handleDelete = (batch: Batch) => {
    setSelectedBatch(batch);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBatch) return;

    try {
      setDeleteLoading(true);
      await batchesAPI.delete(selectedBatch.batch_id);
      addToast({ type: 'success', title: 'Batch deleted successfully' });
      setDeleteModalOpen(false);
      setSelectedBatch(null);
      fetchBatches();
      fetchBatchStats();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', message: error.message || 'Failed to delete batch' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSuccess = () => {
    fetchBatches();
    fetchBatchStats();
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleProductFilter = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      product_id: value === 'all' ? undefined : parseInt(value), 
      page: 1 
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Batch Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg dark:border dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Batches</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{batchStats.total}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <Factory className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-0 shadow-lg dark:border dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">This Month</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{batchStats.thisMonth}</p>
              </div>
              <div className="bg-green-500 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 dark:bg-orange-900/20 border-0 shadow-lg dark:border dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">With Complaints</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{batchStats.withComplaints}</p>
              </div>
              <div className="bg-orange-500 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20 border-0 shadow-lg dark:border dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Recent Batches</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{batchStats.recentBatches}</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-full">
                <Package className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 rounded-2xl dark:border dark:border-gray-700">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">Batches Management</CardTitle>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Batch
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by product name..."
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
              />
            </div>
            <Select value={filters.product_id?.toString() || 'all'} onValueChange={handleProductFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Filter by Product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map((product) => (
                  <SelectItem key={product.product_id} value={product.product_id.toString()}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-700">
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Batch Number</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Product</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Production Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Complaints</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Created</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((batch) => (
                      <TableRow key={batch.batch_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {batch.batch_number}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-gray-900 dark:text-white">{batch.product?.name}</div>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {formatDate(batch.production_date)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${(batch._count?.complaints || 0) > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {batch._count?.complaints || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {formatDate(batch.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(batch)}
                              className="h-8 w-8 p-0 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(batch)}
                              className="h-8 w-8 p-0 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(batch)}
                              className="h-8 w-8 p-0 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="rounded-lg"
                    >
                      Previous
                    </Button>
                    {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <Button
                          key={page}
                          variant={page === pagination.page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className="rounded-lg"
                        >
                          {page}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="rounded-lg"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <BatchFormModal
        batch={selectedBatch}
        products={products}
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedBatch(null);
        }}
        onSuccess={handleFormSuccess}
      />

      <BatchViewModal
        batch={selectedBatch}
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedBatch(null);
        }}
      />

      <BatchDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedBatch(null);
        }}
        onConfirm={handleDeleteConfirm}
        batchNumber={selectedBatch?.batch_number || `BATCH-${selectedBatch?.batch_id.toString().padStart(4, '0')}` || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

export default BatchesPage;